import { useCallback } from 'react';
import { authApi, AuthUser } from '../../services/api';
import { AuthBootstrapContext } from '../../services/sap-context';
import { signInWithNiceOidc } from '../../services/nice-oidc-auth';
import { openOidcPopupBlank } from '../../services/nice-oidc-auth/open-oidc-popup.util';
import { getSapC4cBaseUrl } from '../../utils/config';
import { logger } from '../../utils/logger';

export function useAuthOidcSignIn(
  authBootstrapRef: React.MutableRefObject<AuthBootstrapContext | null>,
  resolveAuthContext: () => Promise<AuthBootstrapContext | null>,
  onAuthenticated: (accessToken: string, expiresIn: number, user: AuthUser) => void
) {
  const resolveSapHost = useCallback((authContext: AuthBootstrapContext | null): string => {
    if (authContext?.sapHost) {
      return authContext.sapHost;
    }

    const configuredHost = getSapC4cBaseUrl()?.trim();
    if (configuredHost) {
      return configuredHost;
    }

    throw new Error('SAP host is not configured for NICE sign-in.');
  }, []);

  const completeOidcSession = useCallback(
    async (
      authContext: AuthBootstrapContext | null,
      oidc: { code: string; codeVerifier: string; redirectUri: string }
    ) => {
      const sapHost = resolveSapHost(authContext);
      const response = await authApi.createOidcSession(sapHost, oidc);
      onAuthenticated(response.accessToken, response.expiresIn, response.user);
      logger.debug('[Auth] NICE OIDC authentication successful for:', response.user.displayName);
    },
    [onAuthenticated, resolveSapHost]
  );

  const signInWithNice = useCallback(async () => {
    const popup = openOidcPopupBlank();

    try {
      const authContext = authBootstrapRef.current ?? (await resolveAuthContext());
      const sapHost = resolveSapHost(authContext);
      const oidc = await signInWithNiceOidc(sapHost, popup);
      await completeOidcSession(authContext, oidc);
    } catch (err) {
      if (!popup.closed) {
        popup.close();
      }
      throw err;
    }
  }, [authBootstrapRef, completeOidcSession, resolveAuthContext, resolveSapHost]);

  return { signInWithNice };
}
